/*
 * Ryan O'Dowd
 * 2022-07-17
 * © Copyright 2022 Oakwood Software Consulting, Inc.  All Rights Reserved.
 */
import React, {
  useMemo,
  useState,
} from 'react';
import {
  ScrollView,
  View,
} from 'react-native';
import Accordion from '@components/common/Accordion';
import {
  DataTable,
} from 'react-native-paper';
import TableHead from './TableHead';
import Text from '@components/common/Text';
import getStyles from './styles';
import hooks from '@hooks';

const ROWS_PER_PAGE = [10, 25, 50, 100];

function descendingComparator(a, b, orderByIndex) {
  if (b[orderByIndex].sortValue < a[orderByIndex].sortValue) {
    return -1;
  }
  if (b[orderByIndex].sortValue > a[orderByIndex].sortValue) {
    return 1;
  }
  return 0;
}

function getComparator(orderDirection, orderByIndex) {
  if (orderByIndex === null) {
    // the table is not sortable
    return () => 0;
  }
  return orderDirection === 'descending'
    ? (a, b) => descendingComparator(a, b, orderByIndex)
    : (a, b) => -descendingComparator(a, b, orderByIndex);
}

const Table = (props: TableProps) => {
  const styles = hooks.useStyles(getStyles);

  const [_pageNum, setPageNum] = useState(0);
  const [_numRowsPerPage, setRowsPerPage] = useState(10);
  const [_orderDirection, setOrderDirection] = React.useState(props.orderDirection || 'descending');
  const [_orderByIndex, setOrderByIndex] = React.useState(props.isSortable ? props.orderByIndex : null);

  const paginationHidden = props.hidePagination || (props.rowData.length < ROWS_PER_PAGE[0]);

  const visibleRows = useMemo(() => {
    // @TODO: can filter here as well as needed (for searching, etc.)
    return [...props.rowData].sort(getComparator(_orderDirection, _orderByIndex)).slice(_pageNum * _numRowsPerPage, _pageNum * _numRowsPerPage + _numRowsPerPage);
  }, [_orderDirection, _orderByIndex, _pageNum, _numRowsPerPage, props.rowData]);

  const emptyRows = _pageNum > 0 ? Math.max(0, (1 + _pageNum) * _numRowsPerPage - props.rowData.length) : 0; // @TODO: still need this?

  const from = _pageNum * _numRowsPerPage;
  const to = Math.min((_pageNum + 1) * _numRowsPerPage, props.rowData.length);

  const dataTable = (
    <DataTable style={styles.container(!paginationHidden)}>
      {props.headerRowData &&
        <TableHead
          headCells={props.headerRowData}
          lightBackground={props.lightBackground}
          isSortable={!!props.isSortable}
          orderDirection={_orderDirection}
          orderByIndex={_orderByIndex}
          onRequestSort={(columnIndex) => {
            if (props.isSortable) {
              const isDesc = _orderByIndex === columnIndex && _orderDirection === 'descending';
              setOrderDirection(isDesc ? 'ascending' : 'descending');
              setOrderByIndex(columnIndex);
              setPageNum(0);
            }
          }}
        />
      }
      {visibleRows.map((row, rowIndex) => {
        return (
          <DataTable.Row
            key={`${row[0].id}_row`}
            style={[
              styles.row(rowIndex, props.lightBackground),
              styles.noBorder,
              props.onRowPress && styles.pressableRow,
              !props.totalsRowsData && ((rowIndex === visibleRows.length - 1)) ? styles.lastRow : undefined,
            ]}
            onPress={!props.onRowPress ? undefined : () => props.onRowPress(row)}
          >
            {row.map((cell, cellIndex) => {
              const cellWidth = styles.globalScale * (props.headerRowData?.[cellIndex]?.width || 0);
              const rightAlign = !!props.headerRowData?.[cellIndex]?.rightAlign;
              return (
                <DataTable.Cell
                  key={cell.id}
                  numeric={rightAlign}
                  style={[styles.cell(rightAlign), cell.styles, cellWidth && {width: cellWidth, minWidth: cellWidth}]} // @TODO: should we have two props cell.styles and cell.textStyles?
                  textStyle={[styles.cellText(rightAlign), cell.textStyles]}
                  {...cell.tableCellProps}
                >
                  {cell.content}
                </DataTable.Cell>
              );
            })}
          </DataTable.Row>
        );
      })}
      {props.totalsRowsData?.map((row, rowIndex) => {
        return (
          <DataTable.Row
            key={`${row[0].id}_row`}
            style={[
              styles.row(visibleRows.length + rowIndex, props.lightBackground),
              styles.noBorder,
              (rowIndex === props.totalsRowsData.length - 1) ? styles.lastRow : undefined,
            ]}
          >
            {row.map((cell, cellIndex) => {
              const rightAlign = !!props.headerRowData?.[cellIndex]?.rightAlign;
              const cellWidth = styles.globalScale * (props.headerRowData?.[cellIndex]?.width || 0);
              return (
                <DataTable.Cell
                  key={cell.id}
                  style={[styles.cell(rightAlign), cell.styles, cellWidth && {width: cellWidth, minWidth: cellWidth}, styles.totalCell]} // @TODO: should we have two props cell.styles and cell.textStyles?
                  textStyle={[styles.cellText(rightAlign), styles.totalText, cell.textStyles]}
                  numeric={rightAlign}
                >
                  {cell.content}
                </DataTable.Cell>
              );
            })}
          </DataTable.Row>
        );
      })}
      {Array.from({length: emptyRows}).map((__, i) => (
        <DataTable.Row
          key={i}
          style={styles.noBorder}// @TODO: guarnatee this is the same height as the other rows
        >
          {props.headerRowData.map((cell) => <DataTable.Cell key={cell.id}>&nbsp;</DataTable.Cell>)}
        </DataTable.Row>
      ))}
    </DataTable>
  );

  const filters = (
    <View style={styles.filtersWrapper}>
      <View style={styles.filtersLeft}>
        {props.filtersJsx}
      </View>
      <View style={styles.filtersRight}>
        {/* @TODO: only show if there is more than one page */}
        {/* @TODO: if hidden, make sure bottom padding is non-zero */}
        <DataTable.Pagination
          style={[
            styles.pagination,
            paginationHidden && styles.notVisible,
          ]}
          page={_pageNum}
          numberOfItemsPerPageList={props.showRowsPerPage && ROWS_PER_PAGE}
          numberOfItemsPerPage={props.showRowsPerPage && _numRowsPerPage}
          numberOfPages={Math.ceil(props.rowData.length / _numRowsPerPage)}
          onPageChange={(newPageNum) => setPageNum(newPageNum)}
          onItemsPerPageChange={(newNum) => {
            setRowsPerPage(newNum);
            setPageNum(0);
          }}
          showFastPaginationControls={true}
          // @TODO: paginationControlRippleColor={styles.colors.accent}
          // @TODO: theme
          // @TODO: dropdownItemRippleColor={styles.colors.accent}
          // @TODO: selectPageDropdownLabel
          label={<Text style={styles.paginationText}>{`${from + 1}-${to} of ${props.rowData.length}`}</Text>}
        />
      </View>
    </View>
  );

  if (props.noAccordion) {
    return (
      <React.Fragment>
        {dataTable}
        {filters}
      </React.Fragment>
    );
  }

  return (
    <Accordion
      defaultOpen={props.open || props.tableTitle === 'Participant progress'}// @TODO: title is too much business logic
      expandable={!props.disableAccordion}
      theme={props.lightBackground ? 'white' : 'primary'}
      title={
        <View style={styles.titleAndSubtitle(props.lightBackground)}>
          <Text style={styles.titleText(props.lightBackground)}>{props.tableTitle}</Text>
          <Text style={styles.subtitleText(props.lightBackground)}>{props.tableSubtitle}</Text>{/* @TODO: should this be a separate prop into accordion? */}
        </View>
      }
      leftItem={props.leftItem}
      hasBadge={props.hasBadge}
      badgeText={props.badgeText}
    >
      <ScrollView
        contentContainerStyle={styles.scrollWrapper}
        horizontal={true}
      >
        {dataTable}
      </ScrollView>
      {filters}
    </Accordion>
  );
};

class TableProps {
  rowData: any[];

  tableTitle?: string | React.ReactNode;
  orderDirection?: 'ascending' | 'descending';
  disableAccordion?: boolean;
  tableSubtitle?: string;
  onRowPress?: (row: any) => void;
  filtersJsx?: any;
  headerRowData?: any[];
  hidePagination?: boolean;
  showRowsPerPage?: boolean;
  open?: boolean;
  totalsRowsData?: any[];
  lightBackground?: boolean;
  isSortable?: boolean;
  orderByIndex?: number;
  noAccordion?: boolean;
  leftItem?: any;
  hasBadge?: boolean;
  badgeText?: string;
};

Table.displayName = 'Table';
export default Table;
